import { DermloopClinicalActionVariantEnum } from '@dermloop/static-values';
import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsEnum,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator';
import { ConditionIndexActionType } from './condition.dto';
//#region Search

/**
 * Range query for dates used for searching lesions.
 */
export class SearchIndexedLesionsUserDateRangeDto {
  @IsOptional()
  @IsDate()
  from?: Date;

  @IsOptional()
  @IsDate()
  to?: Date;
}

const orderOptions = ['asc', 'desc'] as const;
type OrderOption = (typeof orderOptions)[number];

class Sort {
  @IsString()
  field: string;

  @IsIn(orderOptions)
  order: OrderOption;
}

class ActionSearchDto {
  @IsEnum(ConditionIndexActionType, { each: true })
  type: ConditionIndexActionType[];

  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum)
  variant?: DermloopClinicalActionVariantEnum;
}
export class SearchIndexedLesionsDto {
  /**
   * Pagination
   */
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1000)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(1000)
  take: number;

  @IsOptional()
  @ValidateNested()
  sort?: Sort;

  /**
   * Wildcard search (case sensitive)
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  @MaxLength(11)
  ssn?: string;

  /**
   * Wildcard search (case sensitive)
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  mrn?: string;

  /**
   * Wildcard search (case sensitive)
   * Used for searching both first name, last name and full name.
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  name?: string;

  /**
   * Filters
   */
  @IsOptional()
  @IsUUID('all')
  healthRecordId?: string;

  @IsOptional()
  @IsUUID('all', { each: true })
  userIds?: string[];

  @IsOptional()
  @IsUUID('all', { each: true })
  userMedicalUnitIds?: string[];

  @IsOptional()
  @IsUUID('all', { each: true })
  jurisdictionalGroupIds?: string[];

  @IsOptional()
  @IsBoolean()
  userActionResolved?: boolean;

  // If true, filters for lesions that have an accurate diagnosis id null
  // NB! Should not be set if accurateDiagnosisIds is set
  @IsOptional()
  @IsBoolean()
  accurateDiagnosisIdNull?: boolean;

  // true: no accurate diagnosis, or one flagged undiagnosable. false: a diagnosable accurate diagnosis.
  @IsOptional()
  @IsBoolean()
  accurateDiagnosisUndiagnosable?: boolean;

  // NB! Should not be set if accurateDiagnosisIdNull is set
  @IsOptional()
  @IsUUID('all', { each: true })
  accurateDiagnosisIds?: string[];

  @IsOptional()
  @IsUUID('all', { each: true })
  clinicalDiagnosisIds?: string[];

  // Filter to lesions that have user diagnosis === accurate diagnosis
  @IsOptional()
  @IsBoolean()
  correct?: boolean;

  // Filter actions
  @IsOptional()
  @IsEnum(ConditionIndexActionType, { each: true })
  userActionTypes?: ConditionIndexActionType[];

  // Filter action variants
  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum, { each: true })
  @IsArray()
  actionVariants?: DermloopClinicalActionVariantEnum[];

  @IsOptional()
  @Type(() => ActionSearchDto)
  @ValidateNested({ each: true })
  actions?: ActionSearchDto[];

  @IsOptional()
  @IsUUID()
  conditionTypeId?: string;

  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionTypeId */
  skinConditionTypeId?: string;

  // The below is kept as example code for ranges

  /**
   * Ranges (inclusive)
   */
  // @Type(() => SearchIndexedLesionsUserDateRangeDto)
  // @IsOptional()
  // @ValidateNested()
  // userActionDateRange?: SearchIndexedLesionsUserDateRangeDto;

  @IsOptional()
  @IsBoolean()
  isPatientContacted?: boolean;

  @IsOptional()
  @IsBoolean()
  isLatestAction?: boolean;

  @Type(() => SearchIndexedLesionsUserDateRangeDto)
  @IsOptional()
  @ValidateNested()
  followupDateRange?: SearchIndexedLesionsUserDateRangeDto;

  @IsBoolean()
  @IsOptional()
  isDraft?: boolean;

  @IsBoolean()
  @IsOptional()
  isDemoHealthRecord?: boolean;
}

//#endregion

//#region Clinical statistics

/**
 * Clinical statistics for a user in a medical unit.
 */
export interface SearchClinicalStatisticsResultDto {
  // User specific statistics across all medical units
  userStatistics: StatisticBaseValues;

  // Statistics in the medical unit
  medicalUnitStatistics?: StatisticBaseValues;

  // Statistics for all users with the given occupation and specialisation across all medical units
  occupationSpecialisationStatistics?: StatisticBaseValues;

  /** @deprecated Use occupationSpecialisationStatistics. */
  occupationSpecialityStatistics?: StatisticBaseValues;

  // Statistics for all clinical actions in the medical unit
  clinicalActionStatistics?: ClinicalActionStatistics[];

  // Overall benign diagnosis statistics for the user across all medical units
  benignStatistics?: StatisticBaseValues;

  // Overall malignant diagnosis statistics for the user across all medical units
  malignantStatistics?: StatisticBaseValues;

  // Statistics for each benign diagnosis provided in the request for the user across all medical units
  benignDiagnosisStatistics?: StatisticDiagnosisValues[];

  // Statistics for each malignant diagnosis provided in the request for the user across all medical units
  malignantDiagnosisStatistics?: StatisticDiagnosisValues[];

  // afterDate from request dto
  afterDate?: Date;
}

/**
 * Basic statistic values for the confusion matrix.
 */
export interface StatisticBaseValues {
  tp: number;
  tn: number;
  fp: number;
  fn: number;
}

/**
 * Statistics for a clinical action.
 */
export interface ClinicalActionStatistics {
  // The action type
  actionType: ConditionIndexActionType;
  userCount: number;
  medicalUnitCount: number;
}

/**
 * Statistics for a diagnosis.
 */
export interface StatisticDiagnosisValues {
  // If null, it is a group of "other" diagnoses
  diagnosisId?: string;

  statistics: StatisticBaseValues;
}

//#endregion
